import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, FileSpreadsheet } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../components/ui/command';
import { Button } from '../../components/ui/button';
import { useStatsHistory } from '../../features/statistics/hooks/useStatsHistory';
import { useStatsAccount } from '../../features/statistics/hooks/useStatsAccount';
import { useDateRangeParams } from '../../features/statistics/hooks/useDateRangeParams';
import { DateRangeSelector } from '../../features/statistics/components/DateRangeSelector';
import { ExportMenu } from '../../features/statistics/components/ExportMenu';
import { periodDays } from '../../features/statistics/lib/date-range';
import { pickHistoryAccount } from './pick-account';

const toDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;


type Dem = Record<string, number> | null | undefined;

function demTotal(data: Dem) {
  return data ? Object.values(data).reduce((s, v) => s + v, 0) : 0;
}

// 'YYYY-MM-DD' или ISO → «17 июн. 2026»
function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// короткий формат для оси графика: «17 июн.»
function fmtDateShort(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function topEntries(data: Dem, n: number, byLabel = false) {
  if (!data) return [];
  const total = demTotal(data) || 1;
  const entries = Object.entries(data).sort(byLabel ? (a, b) => a[0].localeCompare(b[0]) : (a, b) => b[1] - a[1]);
  return entries.slice(0, n).map(([k, v]) => ({ k, v, pct: Math.round((v / total) * 100) }));
}

// ISO-код страны → эмодзи-флаг
function countryFlag(cc: string): string {
  if (!/^[A-Za-z]{2}$/.test(cc)) return '🌐';
  return String.fromCodePoint(...[...cc.toUpperCase()].map((ch) => 127397 + ch.charCodeAt(0)));
}

const nf = (v: number) => v.toLocaleString('ru-RU');

function RankPanel({ title, data, kind = 'plain', n = 6, byLabel = false }:
  { title: string; data: Dem; kind?: 'country' | 'plain'; n?: number; byLabel?: boolean }) {
  const rows = topEntries(data, n, byLabel);
  const max = rows.reduce((m, r) => Math.max(m, r.v), 0) || 1;
  return (
    <div>
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      {rows.length === 0
        ? <div className="text-sm text-muted-foreground">нет данных</div>
        : (
          <div className="space-y-2.5">
            {rows.map((r, i) => (
              <div key={r.k} className="flex items-center gap-3">
                {kind === 'country'
                  ? <span className="w-5 text-base leading-none">{countryFlag(r.k)}</span>
                  : <span className="w-5 text-center text-[11px] font-medium tabular-nums text-muted-foreground">{i + 1}</span>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium">{r.k}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{nf(r.v)} · {r.pct}%</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted/70">
                    <div className="h-full rounded-full bg-gradient-to-r from-chart-1 to-chart-3 transition-[width] duration-500" style={{ width: `${Math.round((r.v / max) * 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

const TYPE_LABEL: Record<string, string> = {
  REELS: 'Reels', VIDEO: 'Видео', IMAGE: 'Фото', CAROUSEL_ALBUM: 'Карусель',
};
function TypeBadge({ type }: { type: string | null }) {
  if (!type) return <span className="text-muted-foreground">—</span>;
  const label = TYPE_LABEL[type] ?? (type.charAt(0) + type.slice(1).toLowerCase());
  return (
    <span className="inline-flex items-center rounded-lg bg-zinc-900 px-2.5 py-1 text-xs font-semibold text-white">
      {label}
    </span>
  );
}

function Thumb({ src, w = 44, h = 44, fit = 'cover' }: { src: string | null; w?: number; h?: number; fit?: 'cover' | 'contain' }) {
  return (
    <div className="flex items-center justify-center overflow-hidden rounded-md bg-muted ring-1 ring-black/5" style={{ width: w, height: h }}>
      {src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="h-full w-full"
          style={{ objectFit: fit }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      )}
    </div>
  );
}

const GENDER_LABEL: Record<string, string> = { F: 'Женщины', M: 'Мужчины', U: 'Не указан' };
const GENDER_COLOR: Record<string, string> = { F: '#ec4899', M: '#3b82f6', U: '#94a3b8' };

function GenderPanel({ data }: { data: Dem }) {
  const total = demTotal(data);
  const order = ['F', 'M', 'U'].filter((k) => data?.[k] != null);
  return (
    <div>
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Пол</div>
      {total === 0
        ? <div className="text-sm text-muted-foreground">нет данных</div>
        : (
          <>
            <div className="flex h-3 overflow-hidden rounded-full">
              {order.map((k) => (
                <div key={k} title={GENDER_LABEL[k]} style={{ width: `${(data![k] / total) * 100}%`, background: GENDER_COLOR[k] }} />
              ))}
            </div>
            <div className="mt-3 space-y-2">
              {order.map((k) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: GENDER_COLOR[k] }} />
                    {GENDER_LABEL[k]}
                  </span>
                  <span className="tabular-nums text-muted-foreground">{Math.round((data![k] / total) * 100)}% · {nf(data![k])}</span>
                </div>
              ))}
            </div>
          </>
        )}
    </div>
  );
}

export function StatsHistoryPage() {
  const { accounts, publications, audience, series, loading, error, load } = useStatsHistory();
  // Аккаунт — из ?account= или последний выбранный (общий с «Выгрузками»);
  // период — общий для раздела «Статистика».
  const [wantedAccount, setAccountId] = useStatsAccount();
  const accountId = pickHistoryAccount(accounts, wantedAccount);
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const [periodOption, setPeriodOption] = useDateRangeParams();
  const period = periodDays(periodOption);
  const from = toDay(period.from);
  const to = toDay(period.to);

  const selectedAccount = accounts.find((a) => a.account_id === accountId);

  useEffect(() => {
    if (accountId) load(accountId, from, to);
  }, [accountId, from, to, load]);

  // Суммы дневных метрик за период (подписчики — счётчик, не суммируем).
  const seriesTotals = useMemo(
    () => series.reduce(
      (a, p) => ({ views: a.views + p.views, reach: a.reach + p.reach, interactions: a.interactions + p.interactions }),
      { views: 0, reach: 0, interactions: 0 },
    ),
    [series],
  );

  // Список: один (последний) снимок на пост; только посты, опубликованные в выбранном периоде.
  const latestPublications = useMemo(() => {
    const byMedia = new Map<string, typeof publications[number]>();
    for (const p of publications) {
      const pubDay = p.published_at ? p.published_at.slice(0, 10) : null;
      if (!pubDay || pubDay < from || pubDay > to) continue; // фильтр по дате публикации
      const prev = byMedia.get(p.media_id);
      if (!prev || (p.stat_date ?? '') > (prev.stat_date ?? '')) byMedia.set(p.media_id, p);
    }
    const ts = (v: string | null) => (v ? new Date(v).getTime() : 0);
    return Array.from(byMedia.values()).sort((a, b) => ts(b.published_at) - ts(a.published_at));
  }, [publications, from, to]);

  // Сортировка таблицы публикаций по столбцам (по умолчанию — дата публикации, новые сверху).
  type SortKey = 'published_at' | 'media_type' | 'views' | 'reach' | 'likes' | 'comments' | 'shares' | 'saved' | 'stat_date';
  const [sortKey, setSortKey] = useState<SortKey>('published_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const DATE_KEYS: SortKey[] = ['published_at', 'stat_date'];
  const sortedLatest = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (p: typeof latestPublications[number]) => {
      if (DATE_KEYS.includes(sortKey)) return p[sortKey] ? new Date(p[sortKey] as string).getTime() : 0;
      if (sortKey === 'media_type') return p.media_type ?? '';
      return (p[sortKey] as number) ?? 0;
    };
    return [...latestPublications].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (typeof va === 'string' || typeof vb === 'string') return String(va).localeCompare(String(vb)) * dir;
      return (va - vb) * dir;
    });
  }, [latestPublications, sortKey, sortDir]);

  // Динамика выбранного поста: все его снимки по возрастанию даты снимка.
  const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);
  const selectedSeries = useMemo(
    () => publications
      .filter((p) => p.media_id === selectedMediaId)
      .sort((a, b) => (a.stat_date ?? '').localeCompare(b.stat_date ?? ''))
      .map((p) => ({ date: p.stat_date, views: p.views ?? 0, reach: p.reach ?? 0, likes: p.likes ?? 0, comments: p.comments ?? 0 })),
    [publications, selectedMediaId],
  );
  const selectedMeta = latestPublications.find((p) => p.media_id === selectedMediaId);

  const exportCsv = useMemo(() => () => {
    const q = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const out: string[] = [];

    out.push(q(`Период: ${from} — ${to}`));
    out.push('');

    // --- Итоги за период ---
    out.push(q('Итоги за период'));
    out.push('Просмотры,Охват,Взаимодействия');
    out.push([seriesTotals.views, seriesTotals.reach, seriesTotals.interactions].map(q).join(','));
    out.push('');

    // --- Динамика по дням ---
    out.push(q('Динамика по дням'));
    out.push('Дата,Подписчики,Просмотры,Охват,Взаимодействия');
    for (const p of series) {
      out.push([p.date, p.followers, p.views, p.reach, p.interactions].map(q).join(','));
    }
    out.push('');

    // --- Демография (последний снимок) ---
    if (audience) {
      out.push(q(`Аудитория подписчиков (снимок ${audience.stat_date})`));
      const section = (title: string, data: Record<string, number> | null, byLabel = false) => {
        out.push(q(title));
        const total = data ? Object.values(data).reduce((s, v) => s + v, 0) || 1 : 1;
        const entries = data ? Object.entries(data).sort(byLabel ? (a, b) => a[0].localeCompare(b[0]) : (a, b) => b[1] - a[1]) : [];
        for (const [k, v] of entries) out.push([k, v, `${Math.round((v / total) * 100)}%`].map(q).join(','));
        out.push('');
      };
      section('Страны', audience.by_country);
      section('Города', audience.by_city);
      section('Возраст', audience.by_age, true);
      section('Пол', audience.by_gender);
    }

    // --- Публикации (как на экране) ---
    out.push(q('Публикации'));
    out.push('Дата публикации,Тип,Просмотры,Охват,Лайки,Комм.,Репосты,Сохр.,Обновлено,Ссылка');
    for (const p of sortedLatest) {
      out.push([p.published_at ? p.published_at.slice(0, 10) : '', p.media_type, p.views, p.reach, p.likes, p.comments, p.shares, p.saved, p.stat_date, p.permalink].map(q).join(','));
    }

    const blob = new Blob([out.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${accountId}_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sortedLatest, audience, series, seriesTotals, accountId, from, to]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Popover open={accountPickerOpen} onOpenChange={setAccountPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={accountPickerOpen} className="w-64 justify-between">
              {selectedAccount ? selectedAccount.username : 'Аккаунт'}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0">
            <Command>
              <CommandInput placeholder="Поиск аккаунта…" />
              <CommandList>
                <CommandEmpty>Не найдено</CommandEmpty>
                <CommandGroup>
                  {accounts.map((a) => (
                    <CommandItem
                      key={a.account_id}
                      value={a.username}
                      onSelect={() => { setAccountId(a.account_id); setAccountPickerOpen(false); }}
                    >
                      <Check className={`h-4 w-4 ${a.account_id === accountId ? 'opacity-100' : 'opacity-0'}`} />
                      {a.username}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <DateRangeSelector value={periodOption} onChange={setPeriodOption} />
        <ExportMenu
          formats={[{ id: 'csv', label: 'CSV', description: 'Итоги, динамика по дням, аудитория и публикации за период', icon: FileSpreadsheet }]}
          onSelect={exportCsv}
          label="Выгрузить"
          variant="outline"
          disabled={sortedLatest.length === 0}
        />
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading && <div className="text-sm text-muted-foreground">Загрузка…</div>}

      <Card>
        <CardHeader><CardTitle>Динамика по дням</CardTitle></CardHeader>
        <CardContent>
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={fmtDateShort} />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="followers" name="Подписчики" stroke="var(--chart-5)" dot={false} />
                <Line type="monotone" dataKey="views" name="Просмотры" stroke="var(--chart-1)" dot={false} />
                <Line type="monotone" dataKey="reach" name="Охват" stroke="var(--chart-2)" dot={false} />
                <Line type="monotone" dataKey="interactions" name="Взаимодействия" stroke="var(--chart-4)" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 border-t pt-4 text-center sm:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Просмотры</div>
              <div className="text-xl font-bold text-chart-1">{nf(seriesTotals.views)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Охват</div>
              <div className="text-xl font-bold text-chart-2">{nf(seriesTotals.reach)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Взаимодействия</div>
              <div className="text-xl font-bold text-chart-4">{nf(seriesTotals.interactions)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle>Аудитория подписчиков</CardTitle>
          {audience?.stat_date && (
            <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">снимок {fmtDate(audience.stat_date)}</span>
          )}
        </CardHeader>
        <CardContent>
          {!audience
            ? <div className="py-6 text-sm text-muted-foreground">Демография ещё не собрана для этого аккаунта.</div>
            : (
              <div className="grid gap-x-10 gap-y-8 md:grid-cols-2 xl:grid-cols-4">
                <RankPanel title="Страны" data={audience.by_country} kind="country" n={6} />
                <RankPanel title="Города" data={audience.by_city} n={6} />
                <RankPanel title="Возраст" data={audience.by_age} n={7} byLabel />
                <GenderPanel data={audience.by_gender} />
              </div>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Публикации (последние данные · клик = динамика)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="w-12 p-2">Превью</th>
                {([
                  ['published_at', 'Дата публикации'],
                  ['media_type', 'Тип'],
                  ['views', 'Просмотры'],
                  ['reach', 'Охват'],
                  ['likes', 'Лайки'],
                  ['comments', 'Комм.'],
                  ['shares', 'Репосты'],
                  ['saved', 'Сохр.'],
                  ['stat_date', 'Обновлено'],
                ] as [SortKey, string][]).map(([key, label]) => (
                  <th
                    key={key}
                    className="cursor-pointer select-none p-2 hover:text-foreground"
                    onClick={() => toggleSort(key)}
                  >
                    {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
                <th className="p-2">Ссылка</th>
              </tr>
            </thead>
            <tbody>
              {sortedLatest.map((p) => (
                <tr
                  key={p.media_id}
                  className="cursor-pointer border-t hover:bg-muted/50"
                  onClick={() => setSelectedMediaId(p.media_id)}
                >
                  <td className="p-2"><Thumb src={p.archived_thumb || p.thumbnail_url} w={40} h={56} fit="cover" /></td>
                  <td className="p-2">{fmtDate(p.published_at)}</td>
                  <td className="p-2"><TypeBadge type={p.media_type} /></td>
                  <td className="p-2">{p.views ?? 0}</td>
                  <td className="p-2">{p.reach ?? 0}</td>
                  <td className="p-2">{p.likes ?? 0}</td>
                  <td className="p-2">{p.comments ?? 0}</td>
                  <td className="p-2">{p.shares ?? 0}</td>
                  <td className="p-2">{p.saved ?? 0}</td>
                  <td className="p-2 text-muted-foreground">{fmtDate(p.stat_date)}</td>
                  <td className="p-2">{p.permalink ? <a className="text-primary underline" href={p.permalink} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>открыть</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={selectedMediaId != null} onOpenChange={(o) => !o && setSelectedMediaId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Динамика поста{selectedMeta?.published_at ? ` · опубликован ${fmtDate(selectedMeta.published_at)}` : ''}
            </DialogTitle>
          </DialogHeader>

          {selectedMeta && (
            <div className="flex gap-4 rounded-xl border bg-muted/30 p-4">
              <div className="shrink-0">
                <Thumb src={selectedMeta.archived_thumb || selectedMeta.thumbnail_url} w={150} h={200} fit="contain" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex items-center gap-2">
                  <TypeBadge type={selectedMeta.media_type} />
                  <span className="text-xs text-muted-foreground">{fmtDate(selectedMeta.published_at)}</span>
                  {selectedMeta.permalink && (
                    <a href={selectedMeta.permalink} target="_blank" rel="noreferrer" className="ml-auto text-xs text-primary underline">
                      открыть в Instagram
                    </a>
                  )}
                </div>
                <p className="max-h-[150px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed">
                  {selectedMeta.caption || <span className="text-muted-foreground">Без подписи</span>}
                </p>
              </div>
            </div>
          )}

          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={selectedSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={fmtDateShort} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="views" name="Просмотры" stroke="var(--chart-1)" dot={false} />
                <Line type="monotone" dataKey="reach" name="Охват" stroke="var(--chart-2)" dot={false} />
                <Line type="monotone" dataKey="likes" name="Лайки" stroke="var(--chart-5)" dot={false} />
                <Line type="monotone" dataKey="comments" name="Комм." stroke="var(--chart-4)" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="max-h-60 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-2">Дата снимка</th><th className="p-2">Просмотры</th><th className="p-2">Охват</th>
                  <th className="p-2">Лайки</th><th className="p-2">Комм.</th>
                </tr>
              </thead>
              <tbody>
                {selectedSeries.map((s) => (
                  <tr key={s.date} className="border-t">
                    <td className="p-2">{fmtDate(s.date)}</td>
                    <td className="p-2">{s.views}</td>
                    <td className="p-2">{s.reach}</td>
                    <td className="p-2">{s.likes}</td>
                    <td className="p-2">{s.comments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
